import { BaseApi } from '@/providers';
import { getCaptchaToken } from '@/utils';
import type { INewQuote, IQuote, IInvoiceUrl } from '@/types';
import { Auth } from '@aws-amplify/auth';

const getHeaders = (
  recaptchaToken?: string | undefined,
  authToken?: string | undefined
): HeadersInit => ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Authorization: authToken ?? '',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Content-Type': 'application/json',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'X-Recaptcha-Token': recaptchaToken ?? '',
});

const getAuth = async (): Promise<string | undefined> => {
  let authToken;

  try {
    const session = await Auth.currentSession();
    authToken = session.getIdToken().getJwtToken();
  } catch (error) {
    console.log(error);
  }

  return authToken;
};

interface IUpdateQuoteResponse extends Pick<IQuote, 'id' | 'status' | 'total'> {}
export interface IQuoteApi {
  create: (quote: INewQuote) => Promise<{ id: string; status: string; total: string }>;
  get: (id: string) => Promise<IQuote>;
  getInvoice: (id: string) => Promise<IInvoiceUrl>;
  update: (quote: IQuote) => Promise<IUpdateQuoteResponse>;
}

class QuoteApi extends BaseApi implements IQuoteApi {
  public async create(quote: INewQuote): Promise<{ id: string; status: string; total: string }> {
    const token = await getCaptchaToken();

    const authToken = await getAuth();

    let response;

    // eslint-disable-next-line @typescript-eslint/naming-convention, unicorn/prefer-ternary
    if (authToken) {
      response = await fetch(`${this.baseApiUrl}/private/quote`, {
        method: 'PUT',
        headers: getHeaders(token, authToken),
        body: JSON.stringify(quote),
      });
    } else {
      response = await fetch(`${this.baseApiUrl}/quote`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(quote),
      });
    }

    const data = (await response.json()) as unknown;
    if (!response.ok) throw new Error((data as Error).message);
    return data as Promise<{ id: string; status: string; total: string }>;
  }

  public async update(quote: IQuote): Promise<IUpdateQuoteResponse> {
    const token = await getCaptchaToken();
    const authToken = await getAuth();

    let response;

    // eslint-disable-next-line @typescript-eslint/naming-convention, unicorn/prefer-ternary
    if (authToken) {
      response = await fetch(`${this.baseApiUrl}/private/quote/${quote.id}`, {
        method: 'POST',
        headers: getHeaders(token, authToken),
        body: JSON.stringify(quote),
      });
    } else {
      response = await fetch(`${this.baseApiUrl}/quote/${quote.id}`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(quote),
      });
    }

    const data = (await response.json()) as unknown;
    if (!response.ok) throw new Error((data as Error).message);
    return data as Promise<IUpdateQuoteResponse>;
  }

  public async get(id: string): Promise<IQuote> {
    const authToken = await getAuth();

    let response;

    // eslint-disable-next-line @typescript-eslint/naming-convention, unicorn/prefer-ternary
    if (authToken) {
      response = await fetch(`${this.baseApiUrl}/private/quote/${id}`, {
        method: 'GET',
        headers: getHeaders('', authToken),
      });
    } else {
      response = await fetch(`${this.baseApiUrl}/quote/${id}`);
    }

    const statusCode = response.status;

    if (statusCode === 410 || statusCode === 404 || statusCode === 401)
      throw new Error(response.status.toString());

    const data = (await response.json()) as unknown;
    return data as Promise<IQuote>;
  }

  public async getInvoice(id: string): Promise<IInvoiceUrl> {
    const token = await getCaptchaToken();
    const authToken = await getAuth();

    let response;

    // eslint-disable-next-line @typescript-eslint/naming-convention, unicorn/prefer-ternary
    if (authToken) {
      response = await fetch(`${this.baseApiUrl}/private/shipping/${id}/invoice`, {
        method: 'GET',
        headers: getHeaders(token, authToken),
      });
    } else {
      response = await fetch(`${this.baseApiUrl}/shipping/${id}/invoice`, {
        method: 'GET',
        headers: getHeaders(token),
      });
    }

    const data = (await response.json()) as unknown;
    if (!response.ok) throw new Error((data as Error).message);
    return data as Promise<IInvoiceUrl>;
  }
}

export default new QuoteApi();
